import { useMemo, useCallback, type KeyboardEvent } from 'react';
import type { Dimension } from '../useDrillDown';
import { formatDimensionValue } from '../utils';

interface FilterChip {
  key: string;
  label: string;
  value: string;
  displayValue: string;
}

interface FilterChipsProps {
  activeFilters: Record<string, string[]>;
  dimensions: Dimension[];
  onFilterChange: (key: string, values: string[]) => void;
  onClearAll: () => void;
}

export function FilterChips({
  activeFilters,
  dimensions,
  onFilterChange,
  onClearAll,
}: FilterChipsProps): JSX.Element | null {
  // Transform activeFilters into flat chip array
  const activeChips = useMemo<FilterChip[]>(() => {
    const chips: FilterChip[] = [];
    Object.entries(activeFilters).forEach(([key, values]) => {
      if (!values || values.length === 0) return;
      const dim = dimensions.find(d => d.key === key);
      values.forEach((chipValue) => {
        chips.push({
          key,
          label: dim?.label || key,
          value: chipValue,
          displayValue: formatDimensionValue(chipValue, key),
        });
      });
    });
    return chips;
  }, [activeFilters, dimensions]);

  // Count total active filters
  const totalActiveFilters = useMemo(() => {
    return Object.values(activeFilters).reduce(
      (sum, values) => sum + (values?.length || 0),
      0
    );
  }, [activeFilters]);

  // Handle chip dismissal
  const handleDismiss = useCallback(
    (chipKey: string, chipValue: string) => {
      const currentValues = activeFilters[chipKey] || [];
      const newValues = currentValues.filter(v => v !== chipValue);
      onFilterChange(chipKey, newValues);
    },
    [activeFilters, onFilterChange]
  );

  // Handle keyboard dismiss (Enter or Space)
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, chipKey: string, chipValue: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleDismiss(chipKey, chipValue);
      }
    },
    [handleDismiss]
  );

  // Handle keyboard on clear all button
  const handleClearAllKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClearAll();
      }
    },
    [onClearAll]
  );

  // Don't render if no active filters
  if (activeChips.length === 0) {
    return null;
  }

  return (
    <div className="flex gap-2 flex-wrap items-center mt-3" role="region" aria-label="Filtros ativos">
      {activeChips.map((chip, index) => (
        <span
          key={`${chip.key}-${chip.value}-${index}`}
          className="inline-flex items-center gap-1 py-1 px-3 bg-brand-dark border border-brand-primary rounded-full text-xs text-brand-primary font-body"
        >
          <strong className="font-display">{chip.label}:</strong> {chip.displayValue}
          <button
            type="button"
            onClick={() => handleDismiss(chip.key, chip.value)}
            onKeyDown={(e) => handleKeyDown(e, chip.key, chip.value)}
            tabIndex={0}
            role="button"
            aria-label={`Remover filtro ${chip.label}: ${chip.displayValue}`}
            className="bg-transparent border-none cursor-pointer px-1 text-base text-brand-primary hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded"
          >
            ×
          </button>
        </span>
      ))}

      {/* Limpar todos button */}
      <button
        type="button"
        onClick={onClearAll}
        onKeyDown={handleClearAllKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`Limpar todos os ${totalActiveFilters} filtros`}
        className="inline-flex items-center gap-1.5 py-1 px-3 rounded-full text-xs text-error border border-border bg-transparent cursor-pointer font-display hover:bg-brand-dark-lighter transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Limpar todos
        <span className="py-0.5 px-1.5 rounded-full bg-error/20 text-error text-[0.65rem]">
          {totalActiveFilters}
        </span>
      </button>
    </div>
  );
}
